/**
 * Pad a string to the specified length
 */
export function pad(str0, ch, len) {
    let str = str0 + '';
    while (str.length < len) {
        str = ch + str;
    }
    return str;
}
/** Type assert that a value is never */
export function assertNever(_val) {
    // Nothing to do
}
const TIME_VALUE_KEYS = ['S', 's', 'm', 'H', 'h', 'd', 'n', 'a'];
function isKeyOfTimeValues(k) {
    return TIME_VALUE_KEYS.includes(k);
}
const CAN_BE_LARGEST_UNIT = ['H', 'h', 'm', 's'];
function canBeLargestUnit(k) {
    return CAN_BE_LARGEST_UNIT.includes(k);
}
/**
 * Creates time value getters for a given number of milliseconds
 * @param v - Milliseconds (can be negative)
 * @returns Object with getters for various time components
 */
function createTimeValues(v) {
    const negative = v < 0 ? '-' : '';
    v = Math.abs(v);
    const totalSeconds = Math.floor(v / 1000);
    const totalMinutes = Math.floor(v / (1000 * 60));
    const totalHours = Math.floor(v / (1000 * 60 * 60));
    return {
        n: negative,
        S: v % 1000,
        s: (largest) => (largest ? totalSeconds : totalSeconds % 60),
        m: (largest) => (largest ? totalMinutes : totalMinutes % 60),
        H: (largest) => (largest ? totalHours : totalHours % 24),
        // for 12 hour format 0 is replied with 12
        h: (largest) => (largest ? totalHours : totalHours % 12 === 0 ? 12 : totalHours % 12),
        d: (_largest) => Math.floor(v / (1000 * 60 * 60 * 24)),
        a: totalHours % 24 >= 12 ? 'PM' : 'AM',
    };
}
/**
 * Truncates milliseconds to the specified precision and pads with zeros
 * @param v - Milliseconds value
 * @param length - Number of digits (1-3): 1 = 1/100, 2 = 1/10, 3 = milliseconds
 * @returns Padded string representation
 */
function truncateMilliseconds(v, length) {
    if (length > 3)
        throw new Error('"S" can only pad to 3');
    const divisor = [100, 10, 1][length - 1];
    return pad(Math.trunc(v / divisor), '0', length);
}
/**
 * Converts milliseconds to a formatted timestamp string
 * @param v - Milliseconds to convert (can be negative)
 * @param format - Format string with placeholders
 * @returns Formatted timestamp string
 */
export function msToStamp(v, format) {
    const values = createTimeValues(v);
    const result = [];
    let inBracket = false;
    let outBracket = false;
    let largestUnit = null;
    let lastChar = '';
    let accChar = '';
    /**
     * Ensures correct unit inside the bracket
     * @param u - Character to check
     */
    const handleLargestUnit = (u) => {
        if (inBracket && !outBracket) {
            if (!canBeLargestUnit(u))
                throw new Error(`"${u}" can not be set as largest unit`);
            if (largestUnit !== u && largestUnit !== null)
                throw new Error('there can only be one unit inside "[ ]"');
            largestUnit = u;
        }
    };
    /**
     * Processes a character and if needed substitute in the needed value
     * The character is then pushed ton the result array
     * @param c - Character to check
     */
    const processChar = (c) => {
        handleLargestUnit(c);
        if (c !== lastChar) {
            if (isKeyOfTimeValues(lastChar)) {
                if (lastChar === 'n' || lastChar === 'a') {
                    result.push(values[lastChar]);
                }
                else if (lastChar === 'S') {
                    result.push(truncateMilliseconds(values[lastChar], accChar.length));
                }
                else {
                    const isLargestUnit = lastChar === largestUnit;
                    result.push(pad(values[lastChar](isLargestUnit), '0', accChar.length));
                }
            }
            else {
                result.push(accChar);
            }
            accChar = '';
        }
        accChar += c;
        lastChar = c;
    };
    for (const c of format) {
        if (c === '[') {
            if (inBracket)
                throw new Error('"[" was already set');
            if (outBracket)
                throw new Error('"[" can not come after "]"');
            inBracket = true;
            continue;
        }
        if (c === ']') {
            if (!inBracket)
                throw new Error('"[" missing');
            if (outBracket)
                throw new Error('"]" was already set');
            if (largestUnit === null)
                throw new Error('"]" cant be set on empty unit');
            outBracket = true;
            continue;
        }
        processChar(c);
    }
    if (inBracket && !outBracket)
        throw new Error('"[" was never closed');
    processChar('');
    return result.join('');
}
//# sourceMappingURL=Util.js.map